import { useAuth } from '../context/AuthContext';

const ELEVATED_ROLES = ['superadmin_platform', 'admin', 'superviseur', 'responsable'];

/**
 * Calcule les droits d'affichage d'un utilisateur à partir de son rôle et de
 * ses groupes (cf. apps/accounts/permissions.py côté backend, même logique) :
 * un rôle élevé (admin/superviseur/responsable/superadmin) n'est jamais bridé
 * par un groupe complémentaire ; un rôle de base (accueil/consultation) en
 * mode 'group_only' ne voit que ce que son groupe autorise explicitement.
 * 'superviseur' a les mêmes droits qu'admin (cf. ADMIN_ROLES côté backend),
 * seule différence : il bascule entre plusieurs églises assignées au lieu
 * d'être limité à user.church.
 */
const ACCUEIL_ROLES = ['superadmin_platform', 'admin', 'superviseur', 'responsable', 'accueil'];

export default function usePermissions() {
  const { user } = useAuth();
  const role = user?.role;
  const isElevated = ELEVATED_ROLES.includes(role);
  const isAdmin = ['admin', 'superadmin_platform', 'superviseur'].includes(role);
  const isAdminOrResp = ['admin', 'superviseur', 'responsable', 'superadmin_platform'].includes(role);
  const isAccueilOrAbove = ACCUEIL_ROLES.includes(role);

  const inGroupOnlyMode = user?.permission_mode === 'group_only' && (user?.groups || []).length > 0 && !isElevated;

  const hasGroupAccess = (appLabel) => {
    if (user?.permission_mode === 'role_only') return false;
    return (user?.permissions || []).some(p => p.startsWith(`${appLabel}.`));
  };

  const canSee = (appLabel, roleAllowed) =>
    inGroupOnlyMode ? hasGroupAccess(appLabel) : (roleAllowed || hasGroupAccess(appLabel));

  return {
    user, role, isAdmin, isAdminOrResp, isAccueilOrAbove, inGroupOnlyMode, hasGroupAccess, canSee,
    canSeeActivites: canSee('activities', isAccueilOrAbove) || canSee('attendance', isAccueilOrAbove),
    canSeeFideles:   canSee('participants', isAccueilOrAbove),
    canSeeFinance:   canSee('accounting', isAdminOrResp),
    canSeeAdmin:     canSee('accounts', isAdminOrResp) || canSee('churches', isAdminOrResp),
    canSeeChurch:    canSee('churches', isAdmin),
    canSeeNotifs:    canSee('notifications', isAdmin),
  };
}
